import React, { useRef } from "react";
import clsx from "clsx";
import Box from "@material-ui/core/Box";
import AutoSizer from "react-virtualized-auto-sizer";

import IconButton from "@material-ui/core/IconButton";
import ThumbUpIcon from "@material-ui/icons/ThumbUp";
import ThumbDownIcon from "@material-ui/icons/ThumbDown";
import Button from "@material-ui/core/Button";
import DialogContent from "@material-ui/core/DialogContent";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import AppPageTable from "components/AppPageTable";
import AppPageTableHeader from "components/AppPageTableHeader";
import AppPageTableContent from "components/AppPageTableContent";
import AppPageTableRowContent from "components/AppPageTableRowContent";
import TrainerAutocomplete from "components/TrainerAutocomplete";
import Loader from "components/Loader";
import ScrollSync from "components/ScrollSync";
import VirtualList from "components/VirtualList";

import { usePageState } from "./usePageState";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import useStyles from "./styles";

interface IProps {
  query?: string;
}

const PendingRoleRequests = (props: IProps) => {
  const { query } = props;
  const classes = useStyles();
  const tableBodyRef = useRef<HTMLDivElement>(null);

  const {
    trainerSearchDialogOpen,
    setTrainer,
    confirmTrainer,
    cancelApproveRole,
    isLoading,
    userRoleRequests,
    approveUserRoleRequest,
    rejectUserRoleRequest,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage
  } = usePageState(query);

  useInfiniteScroll(
    tableBodyRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    true,
    true
  );

  const renderTableHead = () => {
    return (
      <AppPageTableHeader className={classes.appPageTableHeader}>
        <AppPageTableRowContent
          className={clsx(
            classes.tableHeaderContainer,
            classes.appPageTableRowContent
          )}
        >
          <Box className={classes.rowContent} role="row">
            <Box className={classes.rowCell} role="cell">
              Role Requested
            </Box>
            <Box className={classes.rowCell} role="cell">
              Name
            </Box>
            <Box className={classes.rowCell} role="cell">
              License
            </Box>
            <Box className={classes.rowCell} role="cell">
              Facility
            </Box>
            <Box className={classes.rowCell} role="cell">
              Trainer Name
            </Box>
            <Box
              className={clsx(classes.rowCell, classes.rowCellCenter)}
              role="cell"
            >
              Actions
            </Box>
          </Box>
        </AppPageTableRowContent>
      </AppPageTableHeader>
    );
  };

  const renderRows = (requests: racehorse360.IUserRoleRequest[]) => {
    return requests.map(item => (
      <div key={item.id} className={classes.appPageTableRow}>
        <AppPageTableRowContent className={classes.appPageTableRowContent}>
          <Box className={classes.rowContent}>
            <Box className={classes.rowCell} role="cell">
              {item.role.name}
            </Box>
            <Box className={classes.rowCell} role="cell">
              {item.user.firstName} {item.user.lastName}
            </Box>
            <Box className={classes.rowCell} role="cell">
              {item.user.licenseNumber}
            </Box>
            <Box className={classes.rowCell} role="cell">
              {item.facility?.name || "-"}
            </Box>
            <Box className={classes.rowCell} role="cell">
              {item.trainerName || "-"}
            </Box>
            <Box
              className={clsx(classes.rowCell, classes.rowCellCenter)}
              role="cell"
            >
              <IconButton
                className={classes.actionButton}
                size={"small"}
                onClick={() => approveUserRoleRequest(item)}
              >
                <ThumbUpIcon fontSize={"small"} />
              </IconButton>
              <IconButton
                className={classes.actionButton}
                size={"small"}
                onClick={() => rejectUserRoleRequest(item)}
              >
                <ThumbDownIcon fontSize={"small"} />
              </IconButton>
            </Box>
          </Box>
        </AppPageTableRowContent>
      </div>
    ));
  };

  const renderTable = (data: racehorse360.IUserRoleRequest[]) => {
    return (
      <>
        <AppPageTable rootRef={tableBodyRef}>
          <ScrollSync enabled={true}>
            <AppPageTableHeader className={classes.appPageTableHeader}>
              {renderTableHead()}
            </AppPageTableHeader>
            <AppPageTableContent>
              <AutoSizer>
                {({ height, width }) => (
                  <VirtualList
                    className={classes.virtualList}
                    itemClassName={classes.virtualListItem}
                    count={userRoleRequests.length}
                    itemHeight={46}
                    height={height}
                    width={width}
                    scrollContainerRef={tableBodyRef}
                    itemsBeyond={4}
                  >
                    {renderRows(data)}
                  </VirtualList>
                )}
              </AutoSizer>
            </AppPageTableContent>
          </ScrollSync>
        </AppPageTable>
        {trainerSearchDialogOpen && (
          <Dialog open={trainerSearchDialogOpen} onClose={cancelApproveRole}>
            <DialogTitle>Find Trainer</DialogTitle>
            <DialogContent>
              <TrainerAutocomplete onChange={setTrainer} />
            </DialogContent>
            <DialogActions>
              <Button autoFocus onClick={cancelApproveRole}>
                Cancel
              </Button>
              <Button onClick={confirmTrainer}>Submit</Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  };

  return (
    <>
      {!isLoading && renderTable(userRoleRequests)}
      {isLoading && <Loader overlay className={classes.overlayLoader} />}
      {isFetchingNextPage && <Loader infinite />}
    </>
  );
};

export default PendingRoleRequests;
